import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, DialogControl, Loading } from "spark-core-dx/components";
import { SystemTypes } from "spark-core-dx/services";
import { useResourceDomainContext } from "../../../ResourceDomainProvider";
import CardGridControl from "../../CardGridControl";
import {
  buildDeploymentStepValues,
  buildDestinationDeploymentSavePayload,
  createClientGuid,
  getDeploymentCardTitle,
  getDeploymentRecordId,
  getDestinationDeploymentRows,
  getDestinationIdFromValues,
  getDestinationLabel,
  getDestinationManagerKey,
  getDestinationSetupDefaults,
  getEntityObjectDisplayName,
  getEntityObjectRecordId,
  getSavedDeploymentStepValues,
  hasLoadedManagerMetadata,
  isCoreProfileMetadataReady,
  loadDestinationById,
  loadDestinationManagerMetadata,
  normalizeDeploymentRow,
  resolveMetadataItem,
  saveDestination
} from "./destinationSetupStepUtils";
import {
  asArray,
  buildWizardValidationError,
  getErrorSummary,
  getMetadataFields,
  getOperationItem,
  getOperationItems,
  readValue,
  requireSuccessfulServiceResponse,
  requireServiceResponse
} from "../resourceConfigWizardUtils";
import { LinkedStepHeader } from "./WizardStepCommon";

const ENTITY_FORM_GROUP_ID = "resourceWizardEndpointEntityEndpointV1";
export const ENDPOINT_ENTITY_ROUTES_ICON_KEY = "settings_ethernet";

const ENTITY_SELECTOR_METADATA = {
  Fields: [
    { FieldName: "EntitySelectorName", DisplayName: "Entity Object", ShowOnCard: true, ShowOnGrid: true, GridSequence: 1 },
    { FieldName: "EntitySelectorObject", DisplayName: "Object", ShowOnCard: true, ShowOnGrid: true, GridSequence: 2 },
    { FieldName: "EntitySelectorStatus", DisplayName: "Status", ShowOnCard: true, ShowOnGrid: true, GridSequence: 3 }
  ]
};

const EXECUTION_MODE_OPTIONS = [
  { Text: "Source", Value: "1" },
  { Text: "Warehouse", Value: "2" }
];

const normalizeKey = (value) => String(value || "").trim();

const buildField = (fieldName, displayName, fieldType, dataJson = [], isRequired = false, sequence = 1, description = "") => ({
  FieldName: fieldName,
  DisplayName: displayName,
  Description: description,
  FieldType: fieldType,
  DataJson: dataJson,
  IsRequired: isRequired,
  ShowOnGrid: true,
  ShowOnCard: true,
  DisplaySequence: sequence,
  GridSequence: sequence,
  TabId: "Endpoint|1"
});

export const buildEntityEndpointMetadata = (metadata) => ({
  ...(metadata || {}),
  Fields: [
    {
      ...buildField("EntityObjectId", "Entity Object", SystemTypes.UIFieldType.SearchSelectList, [], true, 1, "Entity object exposed by this endpoint."),
      IsReadOnly: true
    },
    buildField("EntityName", "Entity Route Override", SystemTypes.UIFieldType.TextBox, [], false, 2, "Optional route override."),
    buildField("ExecutionMode", "Execution Mode", SystemTypes.UIFieldType.SelectList, EXECUTION_MODE_OPTIONS, true, 3),
    buildField("EnableWarehouseNativeQuery", "Enable Warehouse Query", SystemTypes.UIFieldType.CheckBox, [], false, 4, "Enables read-only warehouse-native query for this entity when the deployment Native Query Mode is Endpoint Scoped."),
    buildField("SchemaScopeIdentifier", "Schema Scope", SystemTypes.UIFieldType.Label, [], false, 5),
    buildField("EntitySchemaVersion", "Schema Version", SystemTypes.UIFieldType.Label, [], false, 6),
    buildField("IsActive", "Active", SystemTypes.UIFieldType.CheckBox, [], false, 7)
  ]
});

const getActiveDeployment = (deployments = []) =>
  asArray(deployments).find((deployment) => readValue(deployment, "IsDeleted", "isDeleted") !== true && readValue(deployment, "IsActive", "isActive") !== false)
  || asArray(deployments).find((deployment) => readValue(deployment, "IsDeleted", "isDeleted") !== true)
  || asArray(deployments)[0]
  || null;

const getEntityRows = (deployment) =>
  asArray(readValue(deployment, "EntityEndpoints", "entityEndpoints"));

const hasDeploymentWideNativeQuery = (deployment) => {
  const mode = Number(readValue(deployment, "WarehouseNativeQueryMode", "warehouseNativeQueryMode"));
  return mode === 2;
};

const endpointRowKey = (row, index) =>
  readValue(row, "UniqueId", "uniqueId", "_id", "id", "_tempKey") || `endpoint-row-${index}`;

const trimTrailingSlash = (value) => String(value || "").replace(/\/+$/, "");

const isRouteSafe = (value) => /^[A-Za-z0-9_-]+$/.test(String(value || ""));

const getRouteSafeValue = (...values) => asArray(values)
  .map((value) => String(value || "").trim())
  .find((value) => isRouteSafe(value)) || "";

export const resolveClientShortName = (endpoint, deployment) => getRouteSafeValue(
  readValue(endpoint, "ClientShortName", "clientShortName"),
  readValue(deployment, "ClientShortName", "clientShortName")
);

const resolveClientId = (endpoint, deployment) =>
  readValue(endpoint, "ClientId", "clientId")
  || readValue(deployment, "ClientId", "clientId")
  || "";

export const resolveClientShortNameAsync = async ({ endpoint, deployment, clientMgr } = {}) => {
  const direct = resolveClientShortName(endpoint, deployment);
  if (direct) return direct;

  const clientId = resolveClientId(endpoint, deployment);
  if (!clientId || !clientMgr) return "";

  try {
    const result = clientMgr.getItem
      ? await clientMgr.getItem(clientId)
      : await clientMgr.Query?.({ model: "x => x.IsDeleted == false && x._id == clientId", params: { clientId } });
    const client = getOperationItem(result) || getOperationItems(result)[0] || null;
    return getRouteSafeValue(
      readValue(client, "ClientShortName", "clientShortName", "ShortName", "shortName", "SystemName", "systemName", "Name", "name")
    );
  } catch {
    return "";
  }
};

const getConfiguredEndpointApiBaseUri = () => {
  const convokeApiUri = window?.configData?.API_URIS?.CONVOKE || "";
  const apiIndex = convokeApiUri.toLowerCase().lastIndexOf("/api/");
  if (apiIndex >= 0) return trimTrailingSlash(convokeApiUri.slice(0, apiIndex));
  return trimTrailingSlash(convokeApiUri);
};

const buildEndpointPathSegment = (value) => encodeURIComponent(String(value || "").trim());

const getEndpointSchemaScopeIdentifier = (deployment, row = null) => {
  const explicit = getRouteSafeValue(readValue(row, "SchemaScopeIdentifier", "schemaScopeIdentifier"));
  if (explicit) return explicit;

  const rowSchemaVersionId = normalizeKey(readValue(row, "EntitySchemaVersionId", "entitySchemaVersionId", "SchemaVersionId", "schemaVersionId"));
  const scopes = asArray(readValue(deployment, "SchemaScopes", "schemaScopes"))
    .filter((scope) => readValue(scope, "IsDeleted", "isDeleted") !== true && readValue(scope, "IsActive", "isActive") !== false);
  const matchingScope = rowSchemaVersionId
    ? scopes.find((scope) => normalizeKey(readValue(scope, "SchemaVersionId", "schemaVersionId")) === rowSchemaVersionId)
    : null;
  return getRouteSafeValue(
    readValue(matchingScope, "SchemaScopeIdentifier", "schemaScopeIdentifier"),
    readValue(scopes[0], "SchemaScopeIdentifier", "schemaScopeIdentifier")
  );
};

export const buildEndpointEntityUrl = (endpoint, deployment, row, clientShortName = "") => {
  const clientRoute = getRouteSafeValue(clientShortName, resolveClientShortName(endpoint, deployment));
  const deploymentKey = readValue(deployment, "Key", "key", "SystemName", "systemName");
  const schemaScopeIdentifier = getEndpointSchemaScopeIdentifier(deployment, row);
  const entityRoute = readValue(row, "EntityName", "entityName", "EntityRouteOverride", "entityRouteOverride", "DisplayName", "displayName");
  if (!clientRoute || !deploymentKey || !schemaScopeIdentifier || !entityRoute) return "";

  const deploymentApiBaseUri = deployment ? readValue(deployment, "APIBaseURI", "apiBaseURI", "ApiBaseUri", "apiBaseUri") || "" : "";
  const baseUri = trimTrailingSlash(deploymentApiBaseUri) || getConfiguredEndpointApiBaseUri();
  const path = `/api/${buildEndpointPathSegment(clientRoute)}/V1/${buildEndpointPathSegment(deploymentKey)}/${buildEndpointPathSegment(schemaScopeIdentifier)}/${buildEndpointPathSegment(entityRoute)}`;
  return baseUri ? `${baseUri}${path}` : path;
};

const isWarehouseExecutionMode = (row) => String(readValue(row, "ExecutionMode", "executionMode") || "") === "2";

const hasEndpointScopedNativeQuery = (deployment, row) => {
  const mode = Number(readValue(deployment, "WarehouseNativeQueryMode", "warehouseNativeQueryMode"));
  return mode === 3
    && isWarehouseExecutionMode(row)
    && readValue(row, "EnableWarehouseNativeQuery", "enableWarehouseNativeQuery") === true;
};

export const isEndpointHostedApiSurfaceEnabled = (deployment) => {
  const publicSurfaceMode = Number(readValue(deployment, "PublicSurfaceMode", "publicSurfaceMode"));
  if (publicSurfaceMode === 2) return false;
  if (publicSurfaceMode === 1 || publicSurfaceMode === 3) return true;

  const hostedApiEnabled = readValue(deployment, "HostedApiEnabled", "hostedApiEnabled");
  if (hostedApiEnabled === false || String(hostedApiEnabled).trim().toLowerCase() === "false") return false;

  return true;
};

export const buildEndpointEntityRouteCatalog = (endpoint, deployment, row, clientShortName = "") => {
  if (!isEndpointHostedApiSurfaceEnabled(deployment)) return [];

  const entityUrl = buildEndpointEntityUrl(endpoint, deployment, row, clientShortName);
  if (!entityUrl) return [];

  const routes = [
    {
      title: "Entity Data",
      methods: "GET, POST",
      url: entityUrl,
      description: "Reads or queries this entity endpoint."
    },
    {
      title: "Entity Schema",
      methods: "GET",
      url: `${entityUrl}/schema`,
      description: "Returns schema details for this entity endpoint."
    },
    {
      title: "Field Options",
      methods: "GET",
      url: `${entityUrl}/options/{fieldName}`,
      description: "Template route for field option lookups."
    }
  ];

  if (hasEndpointScopedNativeQuery(deployment, row)) {
    routes.push({
      title: "Warehouse Query",
      methods: "POST",
      url: entityUrl,
      description: "Runs a warehouse-native query scoped to this entity endpoint."
    });
  }

  return routes;
};

export const getEndpointEntityCardTitle = (row) => {
  const title = readValue(row, "DisplayName", "displayName")
    || readValue(row, "EntityName", "entityName")
    || readValue(row, "EntityRouteOverride", "entityRouteOverride")
    || "";
  const version = readValue(row, "EntityObjectVersion", "entityObjectVersion", "Version", "version");

  return title && version && !/\([^)]+\)\s*$/.test(title) ? `${title} (${version})` : title;
};

export const deleteEndpointEntityRow = (rows, rowToDelete, keyToDelete = "") => {
  const deleteKey = String(keyToDelete || endpointRowKey(rowToDelete));
  return asArray(rows).filter((row, index) => String(endpointRowKey(row, index)) !== deleteKey);
};

const normalizeEntityRow = (row, deployment) => ({
  ...(row || {}),
  DisplayName: getEndpointEntityCardTitle(row),
  IsNew: Boolean(readValue(row, "_tempKey")) || !endpointRowKey(row),
  IsActive: readValue(row, "IsActive", "isActive") !== false,
  IsDeleted: readValue(row, "IsDeleted", "isDeleted") === true,
  UniqueId: readValue(row, "UniqueId", "uniqueId") || createClientGuid(),
  SchemaDestinationDeploymentId: getDeploymentRecordId(deployment),
  ExecutionMode: readValue(row, "ExecutionMode", "executionMode") || 1,
  EnableWarehouseNativeQuery: readValue(row, "EnableWarehouseNativeQuery", "enableWarehouseNativeQuery") === true,
  EntitySchemaVersionId: readValue(deployment, "CurrentSchemaVersionId", "currentSchemaVersionId") || readValue(row, "EntitySchemaVersionId", "entitySchemaVersionId") || "",
  EntitySchemaVersion: readValue(deployment, "CurrentSchemaVersion", "currentSchemaVersion") || readValue(row, "EntitySchemaVersion", "entitySchemaVersion") || "",
  SchemaScopeIdentifier: readValue(row, "SchemaScopeIdentifier", "schemaScopeIdentifier") || getEndpointSchemaScopeIdentifier(deployment, row)
});

export const buildEndpointEntityRowsFromSelection = ({
  currentRows = [],
  entityObjects = [],
  selectedEntityObjectIds = [],
  deployment = null
}) => {
  const selectedIds = new Set(asArray(selectedEntityObjectIds).map(normalizeKey).filter(Boolean));
  const existingEntityObjectIds = new Set(asArray(currentRows)
    .filter((row) => readValue(row, "IsDeleted", "isDeleted") !== true)
    .map((row) => normalizeKey(readValue(row, "EntityObjectId", "entityObjectId")))
    .filter(Boolean));
  const rowsToAdd = asArray(entityObjects)
    .filter((entityObject) => selectedIds.has(normalizeKey(getEntityObjectRecordId(entityObject))))
    .filter((entityObject) => !existingEntityObjectIds.has(normalizeKey(getEntityObjectRecordId(entityObject))))
    .map((entityObject) => {
      const entityObjectId = getEntityObjectRecordId(entityObject);
      return normalizeEntityRow({
        _tempKey: `endpoint-entity-selection-${Date.now()}-${entityObjectId}`,
        IsNew: true,
        IsActive: true,
        IsDeleted: false,
        UniqueId: createClientGuid(),
        EntityObjectId: entityObjectId,
        DisplayName: getEntityObjectDisplayName(entityObject),
        EntityName: readValue(entityObject, "ObjectName", "objectName", "SystemName", "systemName", "Name", "name") || getEntityObjectDisplayName(entityObject),
        EntityObjectVersion: readValue(entityObject, "Version", "version", "EntityObjectVersion", "entityObjectVersion") || ""
      }, deployment);
    });

  return [...asArray(currentRows), ...rowsToAdd];
};

const EndpointScopeConfiguratorStep = ({
  context,
  onChange,
  onSave,
  onContinue,
  registerStepController
}) => {
  const { wizard, values = {}, readOnly, saving } = context;
  const resourceDomainContext = useResourceDomainContext();
  const coreContext = resourceDomainContext?.coreContext;
  const coreState = resourceDomainContext?.coreState;
  const entityObjectMgr = resourceDomainContext?.managers?.entityObject;
  const productResourceDefaults = useMemo(
    () => getDestinationSetupDefaults(wizard, context.productResourceDefaults),
    [context.productResourceDefaults, wizard]
  );
  const managerKey = getDestinationManagerKey(productResourceDefaults);
  const endpointMgr = resourceDomainContext?.managers?.[managerKey];
  const clientMgr = resourceDomainContext?.managers?.client;
  const savedValues = getSavedDeploymentStepValues(wizard, values);
  const endpointId = getDestinationIdFromValues(wizard, savedValues, productResourceDefaults);
  const endpointLabel = getDestinationLabel(productResourceDefaults);
  const [endpoint, setEndpoint] = useState(null);
  const [deployment, setDeployment] = useState(null);
  const [deployments, setDeployments] = useState([]);
  const [entityRows, setEntityRows] = useState([]);
  const [entityObjects, setEntityObjects] = useState([]);
  const [entitySelectorOpen, setEntitySelectorOpen] = useState(false);
  const [selectedEntityObjectIds, setSelectedEntityObjectIds] = useState([]);
  const [entitySelectorLoading, setEntitySelectorLoading] = useState(false);
  const [entityMetadata, setEntityMetadata] = useState(null);
  const [selectedEntityKey, setSelectedEntityKey] = useState("");
  const [editingEntityRow, setEditingEntityRow] = useState(false);
  const [pendingDeleteEntityRow, setPendingDeleteEntityRow] = useState(null);
  const [routeDialog, setRouteDialog] = useState({ open: false, title: "", routes: [] });
  const [loading, setLoading] = useState(Boolean(endpointId));
  const [localError, setLocalError] = useState("");
  const endpointMgrRef = useRef(endpointMgr);
  const entityObjectMgrRef = useRef(entityObjectMgr);
  const clientMgrRef = useRef(clientMgr);
  const endpointRef = useRef(endpoint);
  const deploymentRef = useRef(deployment);
  const deploymentsRef = useRef(deployments);
  const entityRowsRef = useRef(entityRows);
  const entityObjectsRef = useRef(entityObjects);
  const coreStateRef = useRef(coreState);
  const productResourceDefaultsRef = useRef(productResourceDefaults);
  const loadKeyRef = useRef("");
  const isBusyRef = useRef(false);

  endpointMgrRef.current = endpointMgr;
  entityObjectMgrRef.current = entityObjectMgr;
  clientMgrRef.current = clientMgr;
  endpointRef.current = endpoint;
  deploymentRef.current = deployment;
  deploymentsRef.current = deployments;
  entityRowsRef.current = entityRows;
  entityObjectsRef.current = entityObjects;
  coreStateRef.current = coreState;
  productResourceDefaultsRef.current = productResourceDefaults;
  isBusyRef.current = loading || entitySelectorLoading;

  useEffect(() => {
    let mounted = true;
    const activeMgr = endpointMgrRef.current;
    const loadKey = `${managerKey}:${endpointId || "none"}`;
    if (!endpointId) {
      setEndpoint(null);
      setDeployment(null);
      setDeployments([]);
      setEntityRows([]);
      setLoading(false);
      return undefined;
    }
    if (!activeMgr) {
      setLoading(false);
      setLocalError(`${endpointLabel} service is not available.`);
      return undefined;
    }
    if (!hasLoadedManagerMetadata(activeMgr) && !isCoreProfileMetadataReady(coreContext)) {
      setLoading(true);
      setLocalError("");
      return undefined;
    }
    if (loadKeyRef.current === loadKey) return undefined;
    loadKeyRef.current = loadKey;
    setLoading(true);
    setLocalError("");

    const load = async () => {
      try {
        const [loadedEndpoint, metadataResult] = await Promise.all([
          loadDestinationById({
            manager: activeMgr,
            destinationId: endpointId,
            coreState: coreStateRef.current,
            fallbackMessage: `${endpointLabel} could not be loaded.`
          }),
          loadDestinationManagerMetadata({
            manager: activeMgr,
            coreContext
          })
        ]);
        if (!mounted) return;
        requireServiceResponse(metadataResult, `${endpointLabel} metadata could not be loaded.`);
        const loadedDeployments = getDestinationDeploymentRows(loadedEndpoint, productResourceDefaultsRef.current)
          .map((row) => normalizeDeploymentRow(row, loadedEndpoint, productResourceDefaultsRef.current));
        const activeDeployment = getActiveDeployment(loadedDeployments);
        const clientShortName = await resolveClientShortNameAsync({
          endpoint: loadedEndpoint,
          deployment: activeDeployment,
          clientMgr: clientMgrRef.current
        });
        const endpointWithClientRoute = clientShortName
          ? { ...loadedEndpoint, ClientShortName: clientShortName }
          : loadedEndpoint;
        setEndpoint(endpointWithClientRoute);
        setDeployments(loadedDeployments);
        setDeployment(activeDeployment);
        setEntityRows(getEntityRows(activeDeployment).map((row) => normalizeEntityRow(row, activeDeployment)));
        setEntityMetadata(buildEntityEndpointMetadata(resolveMetadataItem(metadataResult, "SchemaAPIEntityEndpoint")));
      } catch (ex) {
        if (mounted) setLocalError(ex?.message || `${endpointLabel} scope could not be loaded.`);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();
    return () => {
      mounted = false;
      if (loadKeyRef.current === loadKey) loadKeyRef.current = "";
    };
  }, [coreContext, endpointId, endpointLabel, managerKey]);

  const entityFields = useMemo(() => getMetadataFields(entityMetadata), [entityMetadata]);

  const applyEndpointScopeState = useCallback((nextEndpoint, targetDeploymentId = "") => {
    const nextDeployments = getDestinationDeploymentRows(nextEndpoint, productResourceDefaultsRef.current)
      .map((row) => normalizeDeploymentRow(row, nextEndpoint, productResourceDefaultsRef.current));
    const nextDeployment = targetDeploymentId
      ? nextDeployments.find((row) => String(getDeploymentRecordId(row)) === String(targetDeploymentId)) || getActiveDeployment(nextDeployments)
      : getActiveDeployment(nextDeployments);
    const nextEntityRows = getEntityRows(nextDeployment).map((row) => normalizeEntityRow(row, nextDeployment));

    endpointRef.current = nextEndpoint;
    deploymentsRef.current = nextDeployments;
    deploymentRef.current = nextDeployment;
    entityRowsRef.current = nextEntityRows;
    setEndpoint(nextEndpoint);
    setDeployments(nextDeployments);
    setDeployment(nextDeployment);
    setEntityRows(nextEntityRows);
    setSelectedEntityKey("");

    return { nextDeployment, nextDeployments, nextEntityRows };
  }, []);

  const saveScope = useCallback(async (currentValues = values) => {
    const activeMgr = endpointMgrRef.current;
    const currentEndpoint = endpointRef.current;
    const currentDeployment = deploymentRef.current;
    if (!activeMgr) throw new Error(`${endpointLabel} service is not available.`);
    if (!currentEndpoint || !currentDeployment) throw new Error("Save an endpoint deployment before configuring scope.");

    const normalizedEntities = entityRowsRef.current.map((row) => normalizeEntityRow(row, currentDeployment));
    const nextDeployment = {
      ...currentDeployment,
      EntityEndpoints: normalizedEntities
    };
    const nextDeployments = deploymentsRef.current.map((row) =>
      String(getDeploymentRecordId(row)) === String(getDeploymentRecordId(currentDeployment))
        ? nextDeployment
        : row
    );
    const payload = buildDestinationDeploymentSavePayload(currentEndpoint, productResourceDefaultsRef.current, nextDeployments);
    const savedEndpoint = await saveDestination({
      manager: activeMgr,
      destination: payload,
      coreState: coreStateRef.current,
      fallbackMessage: `${endpointLabel} scope could not be saved.`
    });
    const { nextDeployment: savedDeployment, nextDeployments: savedDeployments } = applyEndpointScopeState(savedEndpoint, getDeploymentRecordId(currentDeployment));

    const valuesToSave = {
      ...buildDeploymentStepValues({ currentValues, destination: savedEndpoint, deployments: savedDeployments }),
      EntityEndpointCount: getEntityRows(savedDeployment).filter((row) => readValue(row, "IsDeleted", "isDeleted") !== true).length,
      NativeQueryEnabled: hasDeploymentWideNativeQuery(savedDeployment)
    };
    onChange?.("EntityEndpointCount", valuesToSave.EntityEndpointCount);
    onChange?.("NativeQueryEnabled", valuesToSave.NativeQueryEnabled);
    return valuesToSave;
  }, [applyEndpointScopeState, endpointLabel, onChange, values]);

  const addEntityEndpoint = useCallback(() => {
    const currentEndpoint = endpointRef.current;
    const currentDeployment = deploymentRef.current;
    const activeEntityObjectMgr = entityObjectMgrRef.current;
    const activeSchemaVersionId = readValue(currentEndpoint, "ActiveSchemaVersionId", "activeSchemaVersionId")
      || readValue(currentDeployment, "CurrentSchemaVersionId", "currentSchemaVersionId")
      || "";
    if (!currentDeployment) return;
    if (!activeEntityObjectMgr?.Query) {
      setLocalError("Entity Object service is not available.");
      return;
    }
    if (!activeSchemaVersionId) {
      setLocalError("Select an active schema version before adding endpoint entity scope.");
      return;
    }

    setEntitySelectorLoading(true);
    setLocalError("");
    void activeEntityObjectMgr.Query({
      model: "x => x.IsDeleted == false && x.SchemaVersionId == schemaVersionId",
      params: { schemaVersionId: activeSchemaVersionId }
    })
      .then((entityObjectResult) => {
        requireSuccessfulServiceResponse(entityObjectResult, "Entity Objects could not be loaded.", coreStateRef.current);
        const resolvedEntityObjects = getOperationItems(entityObjectResult)
          .filter((entityObject) => readValue(entityObject, "IsDeleted", "isDeleted") !== true)
          .sort((left, right) => getEntityObjectDisplayName(left).localeCompare(getEntityObjectDisplayName(right)));
        setEntityObjects(resolvedEntityObjects);
        setSelectedEntityObjectIds([]);
        if (!resolvedEntityObjects.length) {
          setLocalError("No Entity Objects are available for the selected active schema version.");
          return;
        }
        setEntitySelectorOpen(true);
      })
      .catch((ex) => {
        setLocalError(getErrorSummary(ex, "Entity Objects could not be loaded."));
      })
      .finally(() => setEntitySelectorLoading(false));
  }, []);

  const closeEntitySelector = useCallback(() => {
    setEntitySelectorOpen(false);
    setSelectedEntityObjectIds([]);
  }, []);

  const confirmSelectedEntityEndpoints = useCallback(() => {
    const currentDeployment = deploymentRef.current;
    const nextRows = buildEndpointEntityRowsFromSelection({
      currentRows: entityRowsRef.current,
      entityObjects: entityObjectsRef.current,
      selectedEntityObjectIds,
      deployment: currentDeployment
    });
    entityRowsRef.current = nextRows;
    setEntityRows(nextRows);
    setSelectedEntityKey("");
    closeEntitySelector();
  }, [closeEntitySelector, selectedEntityObjectIds]);

  const updateEntityRow = useCallback((updatedRow) => {
    setEntityRows((previous) => previous.map((row, index) =>
      String(endpointRowKey(row, index)) === String(endpointRowKey(updatedRow, index))
        ? normalizeEntityRow(updatedRow, deploymentRef.current)
        : row
    ));
  }, []);

  const deleteEntityRow = useCallback((row, key) => {
    if (readOnly) return;
    setEntityRows((previous) => {
      const nextRows = deleteEndpointEntityRow(previous, row, key);
      entityRowsRef.current = nextRows;
      return nextRows;
    });
    setSelectedEntityKey("");
  }, [readOnly]);

  const requestDeleteEntityRow = useCallback((row, key) => {
    if (readOnly) return;
    setPendingDeleteEntityRow({ row, key });
  }, [readOnly]);

  const cancelDeleteEntityRow = useCallback(() => {
    setPendingDeleteEntityRow(null);
  }, []);

  const viewEndpointEntityRoutes = useCallback((row) => {
    const routes = buildEndpointEntityRouteCatalog(endpointRef.current, deploymentRef.current, row, resolveClientShortName(endpointRef.current, deploymentRef.current));
    if (!routes.length) {
      setLocalError("Endpoint entity routes could not be resolved because the endpoint client route key, deployment key, or entity route is missing.");
      return;
    }

    setLocalError("");
    setRouteDialog({
      open: true,
      title: `${getEndpointEntityCardTitle(row) || "Entity Endpoint"} Routes`,
      routes
    });
  }, []);

  const confirmDeleteEntityRow = useCallback(() => {
    if (!pendingDeleteEntityRow) return;
    deleteEntityRow(pendingDeleteEntityRow.row, pendingDeleteEntityRow.key);
    setPendingDeleteEntityRow(null);
  }, [deleteEntityRow, pendingDeleteEntityRow]);

  const endpointEntityHeaderIconActions = useMemo(() => {
    const actions = [{
      Key: "view-endpoint-entity-routes",
      Title: "View Endpoint Routes",
      IconKey: ENDPOINT_ENTITY_ROUTES_ICON_KEY,
      Visible: () => isEndpointHostedApiSurfaceEnabled(deploymentRef.current),
      Disabled: (row) => !buildEndpointEntityRouteCatalog(endpointRef.current, deploymentRef.current, row, resolveClientShortName(endpointRef.current, deploymentRef.current)).length,
      ActionCallback: viewEndpointEntityRoutes
    }];

    if (!readOnly) {
      actions.push({
        Key: "delete-endpoint-entity",
        Title: "Delete Entity Endpoint",
        IconKey: "delete",
        Tone: "danger",
        Disabled: saving || loading || entitySelectorLoading,
        ActionCallback: requestDeleteEntityRow
      });
    }

    return actions;
  }, [entitySelectorLoading, loading, readOnly, requestDeleteEntityRow, saving, viewEndpointEntityRoutes]);

  const entitySelectorRows = useMemo(() => entityObjects.map((entityObject) => {
    const entityObjectId = getEntityObjectRecordId(entityObject);
    return {
      ...entityObject,
      EntitySelectorKey: entityObjectId,
      EntitySelectorName: getEntityObjectDisplayName(entityObject),
      EntitySelectorObject: readValue(entityObject, "ObjectName", "objectName", "SystemName", "systemName", "Name", "name") || entityObjectId,
      EntitySelectorStatus: readValue(entityObject, "IsActive", "isActive") === false ? "Inactive" : "Active"
    };
  }), [entityObjects]);
  const existingEntityObjectIds = useMemo(() => new Set(entityRows
    .filter((row) => readValue(row, "IsDeleted", "isDeleted") !== true)
    .map((row) => normalizeKey(readValue(row, "EntityObjectId", "entityObjectId")))
    .filter(Boolean)), [entityRows]);
  const selectedEntityObjectIdSet = useMemo(
    () => new Set(selectedEntityObjectIds.map(normalizeKey).filter(Boolean)),
    [selectedEntityObjectIds]
  );

  useEffect(() => {
    registerStepController?.({
      get isBusy() {
        return Boolean(isBusyRef.current);
      },
      prepareSave: saveScope,
      prepareContinue: saveScope
    });
    return () => registerStepController?.(null);
  }, [registerStepController, saveScope]);

  const continueAfterSave = async () => {
    try {
      await saveScope(values);
      onContinue?.();
    } catch (ex) {
      setLocalError(getErrorSummary(ex, "Endpoint scope could not be saved."));
      throw buildWizardValidationError(getErrorSummary(ex, "Endpoint scope could not be saved."), { suppressSummary: true });
    }
  };

  const deploymentTitle = deployment ? getDeploymentCardTitle(deployment, productResourceDefaults) : "";

  return (
    <div className="resource-wizard__linked-step">
      <LinkedStepHeader
        title="Endpoint Scope"
        description={`${deploymentTitle || endpointLabel} exposed entity and native warehouse query capabilities.`}
        countText={`${entityRows.length} configured`}
      />
      {localError ? <div className="resource-wizard__inline-error">{localError}</div> : null}
      {loading ? (
        <div className="resource-wizard__linked-step-loading"><Loading /></div>
      ) : !deployment ? (
        <div className="resource-wizard__state">Save an endpoint deployment before configuring scope.</div>
      ) : (
        <div className="resource-wizard__linked-record-catalog resource-wizard__linked-record-catalog--compact">
          <div className="resource-wizard__linked-record-catalog-scroll">
            <CardGridControl
              rows={entityRows}
              metadata={entityMetadata}
              formMetadata={entityMetadata}
              formFields={entityFields}
              rowKey={endpointRowKey}
              cardTitleResolver={getEndpointEntityCardTitle}
              selectedRowKey={selectedEntityKey}
              onSelectedRowKeyChange={setSelectedEntityKey}
              onEditingChange={(isEditing) => setEditingEntityRow(Boolean(isEditing))}
              onRowChange={updateEntityRow}
              headerIconActionsAfterEdit={endpointEntityHeaderIconActions}
              selectedHeaderIconActions={endpointEntityHeaderIconActions}
              toolbarActions={readOnly ? [] : [
                {
                  Key: "add-entity-endpoint",
                  Title: "Add Entity Endpoint",
                  Label: "Add Entity Endpoint",
                  IconKey: "add",
                  Disabled: saving || entitySelectorLoading,
                  ActionCallback: addEntityEndpoint
                }
              ]}
              toggleEnabled={true}
              toggleFieldName="IsActive"
              readOnly={readOnly}
              disabled={saving || entitySelectorLoading}
              emptyContent="No entity endpoints are configured."
              formGroupId={ENTITY_FORM_GROUP_ID}
              minCardWidth="16rem"
              maxColumns={3}
            />
          </div>
        </div>
      )}
      {!editingEntityRow ? (
        <div className="resource-wizard__linked-step-actions">
          <div />
          <div className="resource-wizard__linked-step-actions-right">
            <ButtonControl
              type="standard-small"
              className="resource-wizard__button resource-wizard__button--secondary"
              onClick={() => onSave?.()}
              disabled={readOnly || saving || loading || entitySelectorLoading}
              loading={saving}
            >
              Save
            </ButtonControl>
            <ButtonControl
              type="ok-small"
              className="resource-wizard__button resource-wizard__button--primary"
              onClick={continueAfterSave}
              disabled={readOnly || saving || loading || entitySelectorLoading}
              loading={saving}
            >
              Save & Continue
            </ButtonControl>
          </div>
        </div>
      ) : null}
      <DialogControl
        openDialog={entitySelectorOpen}
        title="Add Entity Endpoint"
        okText="Add Selected"
        cancelText="Cancel"
        onOk={confirmSelectedEntityEndpoints}
        onCancel={closeEntitySelector}
        disableOk={!selectedEntityObjectIds.length || entitySelectorLoading}
      >
        <div className="resource-wizard__entity-scope-selector">
          {entitySelectorLoading ? (
            <div className="resource-wizard__linked-step-loading"><Loading /></div>
          ) : (
            <CardGridControl
              rows={entitySelectorRows}
              metadata={ENTITY_SELECTOR_METADATA}
              rowKey={(row) => getEntityObjectRecordId(row) || row.EntitySelectorKey}
              selectedRowKeys={selectedEntityObjectIds}
              onSelectedRowKeysChange={setSelectedEntityObjectIds}
              selectableRowDisabledResolver={(row) => existingEntityObjectIds.has(normalizeKey(getEntityObjectRecordId(row)))}
              selectable={true}
              multiSelect={true}
              activeFilterDefaultMode="available"
              activeFilterOptions={[
                { key: "available", label: "Available" },
                { key: "selected", label: "Selected" },
                { key: "both", label: "Both" }
              ]}
              activeFilterRowPredicate={(row, { mode, isSelected }) => {
                if (mode === "selected") return isSelected;
                if (mode === "both") return true;
                return !existingEntityObjectIds.has(normalizeKey(getEntityObjectRecordId(row)));
              }}
              toolbarLeadingContent={<span>{selectedEntityObjectIdSet.size} selected</span>}
              emptyContent="No unselected Entity Objects are available for the selected active schema version."
              minCardWidth="15rem"
              maxColumns={3}
            />
          )}
        </div>
      </DialogControl>
      <DialogControl
        openDialog={Boolean(pendingDeleteEntityRow)}
        title="Delete Entity Endpoint"
        okText="Delete"
        cancelText="Cancel"
        onOk={confirmDeleteEntityRow}
        onCancel={cancelDeleteEntityRow}
        disableOk={readOnly}
      >
        <p>
          Delete {getEndpointEntityCardTitle(pendingDeleteEntityRow?.row) || "this entity endpoint"} from this endpoint scope?
        </p>
      </DialogControl>
      <DialogControl
        openDialog={routeDialog.open}
        title={routeDialog.title || "Endpoint Routes"}
        okText="Close"
        cancelText=""
        onOk={() => setRouteDialog({ open: false, title: "", routes: [] })}
        onCancel={() => setRouteDialog({ open: false, title: "", routes: [] })}
        disableCancel={true}
      >
        <div className="resource-wizard__endpoint-route-list">
          {routeDialog.routes.map((route) => (
            <section className="resource-wizard__endpoint-route" key={`${route.methods}:${route.url}`}>
              <div className="resource-wizard__endpoint-route-header">
                <strong>{route.title}</strong>
                <span>{route.methods}</span>
              </div>
              <code>{route.url}</code>
              <p>{route.description}</p>
            </section>
          ))}
        </div>
      </DialogControl>
    </div>
  );
};

export default EndpointScopeConfiguratorStep;
